"use client";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  type Config,
  cookieToInitialState,
  type State,
  useAccount,
  useDisconnect,
  WagmiProvider,
} from "wagmi";
import { WalletChangeModal } from "@/components/core/WalletChangeModal";
import { minimalWagmiConfig } from "@/configs/wagmi-minimal";
import { useAuth } from "@/context/auth";
import { useWallet } from "@/context/wallet";
import { useLazyAppKit } from "@/lib/lazy-appkit";
import { queryClient } from "@/lib/query-client";
import { openWalletConnection } from "@/lib/wallet/open-wallet-connection";

const walletQueryClient = new QueryClient();

function WalletStateSync({
  config,
  isFullConfig,
  loadFullConfig,
}: {
  config: Config;
  isFullConfig: boolean;
  loadFullConfig: () => Promise<Config>;
}) {
  const { address, isConnected } = useAccount();
  const { disconnect: wagmiDisconnect } = useDisconnect();
  const {
    _setConnected,
    _setAddress,
    _setReady,
    _setFullConfigReady,
    _setOpen,
    _setDisconnect,
  } = useWallet();
  const { open: lazyOpen } = useLazyAppKit();

  useEffect(() => {
    _setConnected(isConnected);
    _setAddress(address);
  }, [isConnected, address, _setConnected, _setAddress]);

  useEffect(() => {
    _setReady(true);
    _setDisconnect(() => wagmiDisconnect());
    _setOpen(async () => {
      let connectionConfig = config;
      if (!isFullConfig) {
        try {
          connectionConfig = await loadFullConfig();
        } catch {
          // An installed MetaMask still works on the minimal configuration.
        }
      }
      await openWalletConnection(connectionConfig, lazyOpen);
    });
  }, [
    _setReady,
    _setOpen,
    _setDisconnect,
    wagmiDisconnect,
    lazyOpen,
    config,
    isFullConfig,
    loadFullConfig,
  ]);

  useEffect(() => {
    _setFullConfigReady(isFullConfig);
  }, [isFullConfig, _setFullConfigReady]);

  return null;
}

/**
 * Wipes every cached per-account response the moment a session ends.
 *
 * React Query keys these by path only — `/user/me`, `/user/me/addresses/...` —
 * with no wallet in the key. Nothing used to clear them on an explicit logout,
 * so the previous account's data was still served afterwards: logging out on
 * checkout kept showing that account's shipping address, and logging back in
 * with a different wallet returned the old account's profile. Combined with a
 * protected layout whose auth check is a server-render snapshot, the buyer
 * stayed on a page describing someone else entirely.
 */
function SessionCacheResetEffect() {
  const isLoggedIn = useAuth((s) => s.isLoggedIn);
  const wasLoggedInRef = useRef(isLoggedIn);

  useEffect(() => {
    const wasLoggedIn = wasLoggedInRef.current;
    wasLoggedInRef.current = isLoggedIn;
    if (wasLoggedIn && !isLoggedIn) {
      walletQueryClient.clear();
      queryClient.clear();
    }
  }, [isLoggedIn]);

  return null;
}

function AutoLoginEffect() {
  const { isConnected, address } = useWallet();
  const {
    setIsLoginDialogOpen,
    isLoggedIn,
    user,
    hasExplicitlyLoggedOut,
    setHasExplicitlyLoggedOut,
    logout,
  } = useAuth();
  const wasConnectedRef = useRef(false);
  const prevAddressRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (isConnected) {
      wasConnectedRef.current = true;
      if (!isLoggedIn && !user && !hasExplicitlyLoggedOut) {
        setIsLoginDialogOpen(true);
      } else if (isLoggedIn) {
        setIsLoginDialogOpen(false);
        setHasExplicitlyLoggedOut(false);
      }
    } else if (wasConnectedRef.current && isLoggedIn) {
      wasConnectedRef.current = false;
      logout().then(() => {
        walletQueryClient.clear();
        queryClient.clear();
      });
    }
  }, [
    isConnected,
    isLoggedIn,
    user,
    hasExplicitlyLoggedOut,
    setIsLoginDialogOpen,
    setHasExplicitlyLoggedOut,
    logout,
  ]);

  // React to a connected-account switch (wallet "edit account" → connect).
  // The effect above only depends on `isConnected`, so a same-session account
  // change while staying connected was ignored — and a previously dismissed
  // login (hasExplicitlyLoggedOut=true) kept the login dialog from reopening
  // for the new account, so "nothing happened". When the user is logged in,
  // WalletChangeModal already handles the re-login prompt; here we cover the
  // connected-but-not-logged-in case.
  useEffect(() => {
    const prevAddress = prevAddressRef.current;
    prevAddressRef.current = address;

    const switchedAccount =
      isConnected &&
      !!address &&
      !!prevAddress &&
      address.toLowerCase() !== prevAddress.toLowerCase();

    if (switchedAccount && !isLoggedIn) {
      // Stale per-account data must not leak across the switch.
      walletQueryClient.clear();
      queryClient.clear();
      setHasExplicitlyLoggedOut(false);
      setIsLoginDialogOpen(true);
    }
  }, [
    address,
    isConnected,
    isLoggedIn,
    setHasExplicitlyLoggedOut,
    setIsLoginDialogOpen,
  ]);

  return null;
}

interface WalletLayerInnerProps {
  config: Config;
  cookies: string | null;
  configKey: string;
  isFullConfig: boolean;
  loadFullConfig: () => Promise<Config>;
  children: ReactNode;
}

function WalletLayerInner({
  config,
  cookies,
  configKey,
  isFullConfig,
  loadFullConfig,
  children,
}: WalletLayerInnerProps) {
  let initialState: State | undefined;
  try {
    initialState = cookieToInitialState(config, cookies);
  } catch {
    initialState = undefined;
  }

  return (
    <WagmiProvider config={config} initialState={initialState} key={configKey}>
      <WalletStateSync
        config={config}
        isFullConfig={isFullConfig}
        loadFullConfig={loadFullConfig}
      />
      <SessionCacheResetEffect />
      <AutoLoginEffect />
      <WalletChangeModal />
      {children}
    </WagmiProvider>
  );
}

interface WalletLayerProps {
  cookies: string | null;
  children: ReactNode;
}

export function WalletLayer({ cookies, children }: WalletLayerProps) {
  const [fullConfig, setFullConfig] = useState<Config | null>(null);
  const loadFullConfig = useCallback(async () => {
    const { wagmiAdapter } = await import("@/configs/wagmi");
    setFullConfig(wagmiAdapter.wagmiConfig);
    useWallet.getState()._setFullConfigFailed(false);
    return wagmiAdapter.wagmiConfig;
  }, []);

  useEffect(() => {
    let cancelled = false;

    import("@/configs/wagmi")
      .then(({ wagmiAdapter }) => {
        if (!cancelled) {
          setFullConfig(wagmiAdapter.wagmiConfig);
        }
      })
      .catch((error) => {
        // Without this catch the rejection is swallowed and fullConfigReady
        // stays false forever, so anything that WAITS on it hangs indefinitely
        // — including the maintenance-mode redirect, which would strand users
        // on a half-working site during a freeze instead of /maintenance.
        // The app itself keeps working on minimalWagmiConfig below.
        //
        // Written straight to the store rather than through component state:
        // useWallet is Zustand, so this needs no prop threading, and the flag
        // has to reach consumers that sit ABOVE this provider in the tree.
        if (cancelled) return;
        console.error("Failed to load the full wagmi config", error);
        useWallet.getState()._setFullConfigFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const activeConfig = fullConfig ?? minimalWagmiConfig;
  const configKey = fullConfig ? "full" : "minimal";
  const isFullConfig = fullConfig !== null;

  return (
    <QueryClientProvider client={walletQueryClient}>
      <WalletLayerInner
        config={activeConfig}
        cookies={cookies}
        configKey={configKey}
        isFullConfig={isFullConfig}
        loadFullConfig={loadFullConfig}
      >
        {children}
      </WalletLayerInner>
    </QueryClientProvider>
  );
}
